import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle, AlertCircle, Loader, ShieldAlert } from 'lucide-react';
import api from '../services/api';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [siteLogo, setSiteLogo] = useState(null);

    useEffect(() => {
        api.get('/admin/settings/public')
            .then((res) => { if (res.data.success) setSiteLogo(res.data.data.site_logo); })
            .catch(() => {});
    }, []);

    const passwordStrength = () => {
        if (!password) return null;
        if (password.length < 8) return { level: 'weak', label: 'Too short', color: 'bg-red-500' };
        if (password.length < 12) return { level: 'fair', label: 'Fair', color: 'bg-yellow-500' };
        return { level: 'strong', label: 'Strong', color: 'bg-green-500' };
    };

    const strength = passwordStrength();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setResult({ success: false, message: 'Passwords do not match.' });
            return;
        }
        if (password.length < 8) {
            setResult({ success: false, message: 'Password must be at least 8 characters.' });
            return;
        }

        setLoading(true);
        setResult(null);
        try {
            const res = await api.post('/auth/reset-password', { token, password });
            setResult({ success: res.data.success, message: res.data.message });
            if (res.data.success) {
                setTimeout(() => navigate('/login'), 3000);
            }
        } catch (err) {
            setResult({ success: false, message: err.response?.data?.message || 'Something went wrong. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    // No token in URL
    if (!token) {
        return (
            <div className="min-h-screen bg-hero flex items-center justify-center px-6">
                <div className="glass-card p-10 max-w-md w-full text-center">
                    <ShieldAlert className="w-14 h-14 text-yellow-400 mx-auto mb-4" />
                    <h2 className="text-2xl font-black text-white mb-2">Invalid Link</h2>
                    <p className="text-gray-400 mb-6">This reset link is missing or malformed.</p>
                    <Link to="/forgot-password" className="btn-primary inline-flex items-center gap-2">
                        Request a New Link
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-hero flex items-center justify-center px-6 py-12">
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse-slow"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse-slow delay-200"></div>
            </div>

            <div className="relative z-10 w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/login" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6">
                        <ArrowLeft className="w-5 h-5" />
                        Back to Sign In
                    </Link>

                    {siteLogo ? (
                        <div className="mx-auto mb-4">
                            <img src={siteLogo} alt="The Deep Wave" width="180" className="object-cover rounded-lg mx-auto" style={{ maxWidth: '180px', width: '180px' }} />
                        </div>
                    ) : (
                        <div className="text-6xl mb-4">🌊</div>
                    )}

                    <h1 className="text-4xl font-black gradient-text mb-2">New Password</h1>
                    <p className="text-gray-400">Choose a strong password for your account</p>
                </div>

                <div className="glass-card p-8">
                    {result?.success ? (
                        <div className="text-center py-4">
                            <CheckCircle className="w-14 h-14 text-green-400 mx-auto mb-4" />
                            <h2 className="text-xl font-bold text-white mb-2">Password Updated!</h2>
                            <p className="text-gray-400 text-sm mb-2">{result.message}</p>
                            <p className="text-gray-500 text-xs mb-6">Redirecting to sign in…</p>
                            <Link to="/login" className="btn-primary w-full flex items-center justify-center gap-2">
                                Sign In Now
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {result && !result.success && (
                                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-red-400 text-sm">{result.message}</p>
                                        {result.message.includes('expired') || result.message.includes('invalid') ? (
                                            <Link to="/forgot-password" className="text-xs text-purple-400 hover:text-purple-300 mt-1 inline-block">
                                                Request a new reset link →
                                            </Link>
                                        ) : null}
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                    New Password
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="input-field pl-12 pr-12"
                                        placeholder="At least 8 characters"
                                        required
                                        disabled={loading}
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>

                                {/* Strength indicator */}
                                {strength && (
                                    <div className="mt-2 flex items-center gap-2">
                                        <div className="flex gap-1 flex-1">
                                            {['weak', 'fair', 'strong'].map((lvl, i) => (
                                                <div
                                                    key={lvl}
                                                    className={`h-1 flex-1 rounded-full transition-colors ${
                                                        ['weak', 'fair', 'strong'].indexOf(strength.level) >= i
                                                            ? strength.color
                                                            : 'bg-gray-700'
                                                    }`}
                                                />
                                            ))}
                                        </div>
                                        <span className="text-xs text-gray-400">{strength.label}</span>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                    Confirm Password
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type={showConfirm ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className={`input-field pl-12 pr-12 ${
                                            confirmPassword && password !== confirmPassword
                                                ? 'border-red-500/50'
                                                : confirmPassword && password === confirmPassword
                                                ? 'border-green-500/50'
                                                : ''
                                        }`}
                                        placeholder="Repeat your new password"
                                        required
                                        disabled={loading}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirm(!showConfirm)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                    >
                                        {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                {confirmPassword && password !== confirmPassword && (
                                    <p className="text-red-400 text-xs mt-1">Passwords don't match</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={loading || !password || !confirmPassword || password !== confirmPassword || password.length < 8}
                                className="w-full btn-primary flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <><Loader className="w-5 h-5 animate-spin" /> Updating…</>
                                ) : (
                                    'Set New Password'
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
